'use client';
import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

// ─── Types ────────────────────────────────────────────────────────────────────
interface SharedGoal {
  id: string;
  title: string;
  description: string | null;
  category: string;
  creator_id: string;
  start_date: string;
  end_date: string;
  target_days: number;
  status: 'active' | 'completed' | 'abandoned';
  emoji: string;
  created_at: string;
  partner_count?: number;
  my_progress?: number;
  is_creator?: boolean;
}

interface GoalPartner {
  id: string;
  user_id: string;
  invite_status: 'pending' | 'accepted' | 'declined';
  joined_at: string | null;
  user_profiles?: { full_name: string; email: string } | null;
  progress_days?: number;
}

interface GoalMilestone {
  id: string;
  goal_id: string;
  day_number: number;
  title: string;
  description: string | null;
  status: 'pending' | 'celebrated' | 'missed';
  celebrated_at: string | null;
}

interface GoalCelebration {
  id: string;
  user_id: string;
  message: string | null;
  emoji: string;
  created_at: string;
  user_profiles?: { full_name: string } | null;
}

type ActiveTab = 'my-goals' | 'create' | 'invites';

const GOAL_CATEGORIES = [
  { value: 'prayer', label: 'Prayer', emoji: '🕌' },
  { value: 'quran', label: 'Quran', emoji: '📖' },
  { value: 'dhikr', label: 'Dhikr', emoji: '📿' },
  { value: 'fasting', label: 'Fasting', emoji: '🌙' },
  { value: 'charity', label: 'Charity', emoji: '💝' },
  { value: 'fitness', label: 'Fitness', emoji: '💪' },
  { value: 'learning', label: 'Learning', emoji: '🎓' },
  { value: 'general', label: 'General', emoji: '🎯' },
];

const MILESTONE_DAYS = [7, 14, 21, 30];

function daysElapsed(startDate: string): number {
  const start = new Date(startDate);
  const now = new Date();
  return Math.max(0, Math.floor((now.getTime() - start.getTime()) / 86400000));
}

function daysRemaining(endDate: string): number {
  const end = new Date(endDate);
  const now = new Date();
  return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / 86400000));
}

function progressPercent(startDate: string, targetDays: number): number {
  const elapsed = daysElapsed(startDate);
  return Math.min(100, Math.round((elapsed / targetDays) * 100));
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function CategoryBadge({ category }: { category: string }) {
  const cat = GOAL_CATEGORIES.find((c) => c.value === category) || GOAL_CATEGORIES[7];
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-xs text-muted-foreground font-500">
      {cat.emoji} {cat.label}
    </span>
  );
}

function ProgressRing({ percent, size = 56 }: { percent: number; size?: number }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (percent / 100) * circ;
  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--muted)" strokeWidth={4} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke="var(--primary)" strokeWidth={4}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
    </svg>
  );
}

function MilestoneTrack({ milestones, elapsed }: { milestones: GoalMilestone[]; elapsed: number }) {
  return (
    <div className="flex items-center gap-1 mt-3">
      {MILESTONE_DAYS.map((day) => {
        const ms = milestones.find((m) => m.day_number === day);
        const reached = elapsed >= day;
        const celebrated = ms?.status === 'celebrated';
        return (
          <div key={day} className="flex flex-col items-center gap-1 flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-700 border-2 transition-all
              ${celebrated ? 'bg-amber-400 border-amber-400 text-white' : reached ?'bg-primary border-primary text-white': 'bg-muted border-border text-muted-foreground'}`}>
              {celebrated ? '🎉' : day}
            </div>
            <span className="text-[10px] text-muted-foreground">Day {day}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function SharedGoalsPage() {
  const { user } = useAuth();
  const supabase = createClient();

  const [activeTab, setActiveTab] = useState<ActiveTab>('my-goals');
  const [goals, setGoals] = useState<SharedGoal[]>([]);
  const [invites, setInvites] = useState<SharedGoal[]>([]);
  const [selectedGoal, setSelectedGoal] = useState<SharedGoal | null>(null);
  const [partners, setPartners] = useState<GoalPartner[]>([]);
  const [milestones, setMilestones] = useState<GoalMilestone[]>([]);
  const [celebrations, setCelebrations] = useState<GoalCelebration[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [celebrating, setCelebrating] = useState<string | null>(null);
  const [celebrationMsg, setCelebrationMsg] = useState('');
  const [todayCheckedIn, setTodayCheckedIn] = useState(false);

  // Create form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'prayer',
    emoji: '🎯',
    partnerEmail: '',
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  const fetchGoals = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Goals I created
      const { data: created } = await supabase
        .from('shared_goals')
        .select('*')
        .eq('creator_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      // Goals I'm a partner in
      const { data: partnerGoalIds } = await supabase
        .from('goal_partners')
        .select('goal_id')
        .eq('user_id', user.id)
        .eq('invite_status', 'accepted');

      const partnerIds = partnerGoalIds?.map((p) => p.goal_id) || [];
      let partnerGoals: SharedGoal[] = [];
      if (partnerIds.length > 0) {
        const { data: pg } = await supabase
          .from('shared_goals')
          .select('*')
          .in('id', partnerIds)
          .neq('creator_id', user.id)
          .eq('status', 'active');
        partnerGoals = pg || [];
      }

      // Pending invites
      const { data: pendingInvites } = await supabase
        .from('goal_partners')
        .select('goal_id')
        .eq('user_id', user.id)
        .eq('invite_status', 'pending');

      const pendingIds = pendingInvites?.map((p) => p.goal_id) || [];
      let inviteGoals: SharedGoal[] = [];
      if (pendingIds.length > 0) {
        const { data: ig } = await supabase
          .from('shared_goals')
          .select('*')
          .in('id', pendingIds);
        inviteGoals = ig || [];
      }

      const allGoals = [
        ...(created || []).map((g) => ({ ...g, is_creator: true })),
        ...partnerGoals.map((g) => ({ ...g, is_creator: false })),
      ];

      // Fetch progress counts
      const goalIds = allGoals.map((g) => g.id);
      if (goalIds.length > 0) {
        const { data: progressData } = await supabase
          .from('goal_progress')
          .select('goal_id, user_id')
          .in('goal_id', goalIds)
          .eq('user_id', user.id)
          .eq('completed', true);

        const progressMap: Record<string, number> = {};
        progressData?.forEach((p) => {
          progressMap[p.goal_id] = (progressMap[p.goal_id] || 0) + 1;
        });

        allGoals.forEach((g) => {
          g.my_progress = progressMap[g.id] || 0;
        });
      }

      setGoals(allGoals);
      setInvites(inviteGoals);
    } catch (err) {
      console.error('Error fetching goals:', err);
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const openGoalDetail = async (goal: SharedGoal) => {
    setSelectedGoal(goal);
    setDetailLoading(true);
    try {
      const [partnersData, milestonesData, celebrationsData, todayProgress] =
        await Promise.all([
          supabase
            .from('goal_partners')
            .select('*, user_profiles(full_name, email)')
            .eq('goal_id', goal.id)
            .eq('invite_status', 'accepted'),
          supabase
            .from('goal_milestones')
            .select('*')
            .eq('goal_id', goal.id)
            .order('day_number'),
          supabase
            .from('goal_celebrations')
            .select('*, user_profiles(full_name)')
            .eq('goal_id', goal.id)
            .order('created_at', { ascending: false })
            .limit(20),
          supabase
            .from('goal_progress')
            .select('id')
            .eq('goal_id', goal.id)
            .eq('user_id', user?.id || '')
            .eq('check_in_date', new Date().toISOString().split('T')[0])
            .maybeSingle(),
        ]);

      setPartners(partnersData || []);
      setMilestones(milestonesData || []);
      setCelebrations(celebrationsData || []);
      setTodayCheckedIn(!!todayProgress);
    } catch (err) {
      console.error('Error loading goal detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!selectedGoal || !user || todayCheckedIn) return;
    setCheckingIn(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const { error } = await supabase.from('goal_progress').insert({
        goal_id: selectedGoal.id,
        user_id: user.id,
        check_in_date: today,
        completed: true,
      });
      if (!error) {
        setTodayCheckedIn(true);
        setGoals((prev) =>
          prev.map((g) =>
            g.id === selectedGoal.id ? { ...g, my_progress: (g.my_progress || 0) + 1 } : g
          )
        );
        setSelectedGoal((prev) =>
          prev ? { ...prev, my_progress: (prev.my_progress || 0) + 1 } : prev
        );

        // Check if a milestone is reached
        const elapsed = daysElapsed(selectedGoal.start_date) + 1;
        const reachedMilestone = milestones.find(
          (m) => m.day_number === elapsed && m.status === 'pending'
        );
        if (reachedMilestone) {
          await supabase
            .from('goal_milestones')
            .update({ status: 'celebrated', celebrated_at: new Date().toISOString() })
            .eq('id', reachedMilestone.id);
          setMilestones((prev) =>
            prev.map((m) =>
              m.id === reachedMilestone.id
                ? { ...m, status: 'celebrated', celebrated_at: new Date().toISOString() }
                : m
            )
          );
        }
      }
    } catch (err) {
      console.error('Check-in error:', err);
    } finally {
      setCheckingIn(false);
    }
  };

  const handleCelebrate = async (milestoneId: string) => {
    if (!selectedGoal || !user || !celebrationMsg.trim()) return;
    setCelebrating(milestoneId);
    try {
      const { data, error } = await supabase
        .from('goal_celebrations')
        .insert({
          goal_id: selectedGoal.id,
          milestone_id: milestoneId,
          user_id: user.id,
          message: celebrationMsg.trim(),
          emoji: '🎉',
        })
        .select('*, user_profiles(full_name)')
        .single();
      if (!error && data) {
        setCelebrations((prev) => [data, ...prev]);
        setCelebrationMsg('');
      }
    } catch (err) {
      console.error('Celebration error:', err);
    } finally {
      setCelebrating(null);
    }
  };

  const handleCreateGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !form.title.trim()) return;
    setCreating(true);
    setCreateError('');
    setCreateSuccess('');
    try {
      const startDate = new Date().toISOString().split('T')[0];
      const endDate = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];

      const { data: goal, error: goalError } = await supabase
        .from('shared_goals')
        .insert({
          title: form.title.trim(),
          description: form.description.trim() || null,
          category: form.category,
          creator_id: user.id,
          start_date: startDate,
          end_date: endDate,
          target_days: 30,
          emoji: GOAL_CATEGORIES.find((c) => c.value === form.category)?.emoji || '🎯',
        })
        .select()
        .single();

      if (goalError || !goal) {
        setCreateError('Failed to create goal. Please try again.');
        return;
      }

      // Add creator as partner
      await supabase.from('goal_partners').insert({
        goal_id: goal.id,
        user_id: user.id,
        invite_status: 'accepted',
        invited_by: user.id,
        joined_at: new Date().toISOString(),
      });

      // Create default milestones
      const defaultMilestones = MILESTONE_DAYS.map((day) => ({
        goal_id: goal.id,
        day_number: day,
        title: day === 7 ? '7-Day Streak 🌟' : day === 14 ? 'Halfway There 💪' : day === 21 ? '21-Day Habit 🔥' : 'Goal Complete! 🏆',
        description: `Day ${day} milestone reached!`,
        status: 'pending' as const,
      }));
      await supabase.from('goal_milestones').insert(defaultMilestones);

      // Invite partner by email if provided
      if (form.partnerEmail.trim()) {
        const { data: partnerProfile } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('email', form.partnerEmail.trim())
          .maybeSingle();

        if (partnerProfile) {
          await supabase.from('goal_partners').insert({
            goal_id: goal.id,
            user_id: partnerProfile.id,
            invite_status: 'pending',
            invited_by: user.id,
          });
          setCreateSuccess(`Goal created! Invitation sent to ${form.partnerEmail}.`);
        } else {
          setCreateSuccess('Goal created! Partner email not found — they need to join Rafeeq first.');
        }
      } else {
        setCreateSuccess('Goal created successfully!');
      }

      setForm({ title: '', description: '', category: 'prayer', emoji: '🎯', partnerEmail: '' });
      await fetchGoals();
      setTimeout(() => {
        setActiveTab('my-goals');
        setCreateSuccess('');
      }, 2000);
    } catch (err) {
      setCreateError('Something went wrong. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleInviteResponse = async (goalId: string, accept: boolean) => {
    if (!user) return;
    try {
      await supabase
        .from('goal_partners')
        .update({
          invite_status: accept ? 'accepted' : 'declined',
          joined_at: accept ? new Date().toISOString() : null,
        })
        .eq('goal_id', goalId)
        .eq('user_id', user.id);
      await fetchGoals();
    } catch (err) {
      console.error('Invite response error:', err);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <AppLayout activeRoute="/shared-goals">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-700 text-foreground">30-Day Shared Goals</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Create goals with accountability partners, track progress together, and celebrate milestones.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted rounded-xl p-1 mb-6 w-fit">
          {([
            { key: 'my-goals', label: 'My Goals', icon: 'FlagIcon' },
            { key: 'create', label: 'Create Goal', icon: 'PlusCircleIcon' },
            { key: 'invites', label: `Invites${invites.length > 0 ? ` (${invites.length})` : ''}`, icon: 'EnvelopeIcon' },
          ] as { key: ActiveTab; label: string; icon: string }[]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-500 transition-all duration-150
                ${activeTab === tab.key ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={15} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── My Goals Tab ── */}
        {activeTab === 'my-goals' && (
          <div>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : goals.length === 0 ? (
              <div className="text-center py-16 bg-card rounded-2xl border border-border">
                <div className="text-5xl mb-3">🎯</div>
                <p className="text-foreground font-600 text-lg">No active goals yet</p>
                <p className="text-muted-foreground text-sm mt-1 mb-4">Create your first 30-day shared goal with an accountability partner.</p>
                <button
                  onClick={() => setActiveTab('create')}
                  className="px-5 py-2.5 bg-primary text-white rounded-xl text-sm font-600 hover:opacity-90 transition-opacity"
                >
                  Create a Goal
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {goals.map((goal) => {
                  const elapsed = daysElapsed(goal.start_date);
                  const remaining = daysRemaining(goal.end_date);
                  const pct = progressPercent(goal.start_date, goal.target_days);
                  return (
                    <div
                      key={goal.id}
                      className="bg-card border border-border rounded-2xl p-5 cursor-pointer hover:border-primary/40 transition-all duration-150 shadow-card"
                      onClick={() => openGoalDetail(goal)}
                    >
                      <div className="flex items-start gap-4">
                        <div className="relative flex-shrink-0">
                          <ProgressRing percent={pct} size={60} />
                          <span className="absolute inset-0 flex items-center justify-center text-xl">
                            {goal.emoji}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-700 text-foreground text-base truncate">{goal.title}</h3>
                            {goal.is_creator && (
                              <span className="text-[10px] px-2 py-0.5 bg-primary/10 text-primary rounded-full font-600">Creator</span>
                            )}
                            <CategoryBadge category={goal.category} />
                          </div>
                          {goal.description && (
                            <p className="text-muted-foreground text-sm mt-0.5 line-clamp-1">{goal.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Icon name="CalendarDaysIcon" size={12} />
                              Day {elapsed} of {goal.target_days}
                            </span>
                            <span className="flex items-center gap-1">
                              <Icon name="CheckCircleIcon" size={12} />
                              {goal.my_progress || 0} check-ins
                            </span>
                            <span className="flex items-center gap-1 text-primary font-500">
                              {remaining}d left
                            </span>
                          </div>
                          {/* Progress bar */}
                          <div className="mt-2.5 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full transition-all duration-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                        <Icon name="ChevronRightIcon" size={16} className="text-muted-foreground flex-shrink-0 mt-1" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Create Goal Tab ── */}
        {activeTab === 'create' && (
          <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
            <h2 className="font-700 text-foreground text-lg mb-5">Create a 30-Day Goal</h2>
            <form onSubmit={handleCreateGoal} className="space-y-4">
              <div>
                <label className="block text-sm font-600 text-foreground mb-1.5">Goal Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Pray Fajr on time every day"
                  className="w-full px-4 py-2.5 bg-muted border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-600 text-foreground mb-1.5">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="What does success look like for this goal?"
                  rows={3}
                  className="w-full px-4 py-2.5 bg-muted border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-600 text-foreground mb-1.5">Category</label>
                <div className="grid grid-cols-4 gap-2">
                  {GOAL_CATEGORIES.map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, category: cat.value }))}
                      className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border text-xs font-500 transition-all
                        ${form.category === cat.value
                          ? 'border-primary bg-primary/10 text-primary' :'border-border bg-muted text-muted-foreground hover:border-primary/40'}`}
                    >
                      <span className="text-lg">{cat.emoji}</span>
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-600 text-foreground mb-1.5">
                  Invite Accountability Partner (optional)
                </label>
                <input
                  type="email"
                  value={form.partnerEmail}
                  onChange={(e) => setForm((f) => ({ ...f, partnerEmail: e.target.value }))}
                  placeholder="partner@email.com"
                  className="w-full px-4 py-2.5 bg-muted border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                />
                <p className="text-xs text-muted-foreground mt-1">They must already have a Rafeeq account.</p>
              </div>

              {createError && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-500">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {createError}
                </div>
              )}
              {createSuccess && (
                <div className="flex items-center gap-2 px-4 py-3 bg-green-500/10 border border-green-500/20 rounded-xl text-sm text-green-600">
                  <Icon name="CheckCircleIcon" size={16} />
                  {createSuccess}
                </div>
              )}

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="submit"
                  disabled={creating || !form.title.trim()}
                  className="flex-1 py-3 bg-primary text-white rounded-xl text-sm font-600 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {creating ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icon name="PlusCircleIcon" size={16} />
                  )}
                  {creating ? 'Creating...' : 'Start 30-Day Goal'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── Invites Tab ── */}
        {activeTab === 'invites' && (
          <div>
            {invites.length === 0 ? (
              <div className="text-center py-16 bg-card rounded-2xl border border-border">
                <div className="text-5xl mb-3">📬</div>
                <p className="text-foreground font-600 text-lg">No pending invites</p>
                <p className="text-muted-foreground text-sm mt-1">When someone invites you to a shared goal, it will appear here.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {invites.map((goal) => (
                  <div key={goal.id} className="bg-card border border-border rounded-2xl p-5 shadow-card">
                    <div className="flex items-start gap-3">
                      <span className="text-3xl">{goal.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-700 text-foreground">{goal.title}</h3>
                        {goal.description && (
                          <p className="text-muted-foreground text-sm mt-0.5">{goal.description}</p>
                        )}
                        <CategoryBadge category={goal.category} />
                        <p className="text-xs text-muted-foreground mt-2">
                          30-day goal starting {new Date(goal.start_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={() => handleInviteResponse(goal.id, true)}
                        className="flex-1 py-2.5 bg-primary text-white rounded-xl text-sm font-600 hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                      >
                        <Icon name="CheckIcon" size={14} /> Accept
                      </button>
                      <button
                        onClick={() => handleInviteResponse(goal.id, false)}
                        className="flex-1 py-2.5 bg-muted text-muted-foreground rounded-xl text-sm font-600 hover:text-foreground transition-colors flex items-center justify-center gap-2"
                      >
                        <Icon name="XMarkIcon" size={14} /> Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Goal Detail Drawer ── */}
      {selectedGoal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedGoal(null)} />
          <div className="relative bg-card border border-border rounded-t-3xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Drawer header */}
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center gap-3 z-10">
              <span className="text-3xl">{selectedGoal.emoji}</span>
              <div className="flex-1 min-w-0">
                <h2 className="font-700 text-foreground text-lg truncate">{selectedGoal.title}</h2>
                <CategoryBadge category={selectedGoal.category} />
              </div>
              <button
                onClick={() => setSelectedGoal(null)}
                className="p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>

            {detailLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="px-6 py-5 space-y-6">
                {/* Progress overview */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Day', value: daysElapsed(selectedGoal.start_date), sub: `of ${selectedGoal.target_days}` },
                    { label: 'Check-ins', value: selectedGoal.my_progress || 0, sub: 'completed' },
                    { label: 'Days Left', value: daysRemaining(selectedGoal.end_date), sub: 'remaining' },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-muted rounded-xl p-3 text-center">
                      <div className="text-2xl font-700 text-foreground">{stat.value}</div>
                      <div className="text-xs text-muted-foreground">{stat.sub}</div>
                      <div className="text-[10px] text-muted-foreground font-500 mt-0.5">{stat.label}</div>
                    </div>
                  ))}
                </div>

                {/* Check-in button */}
                <button
                  onClick={handleCheckIn}
                  disabled={todayCheckedIn || checkingIn}
                  className={`w-full py-3.5 rounded-xl text-sm font-700 flex items-center justify-center gap-2 transition-all
                    ${todayCheckedIn
                      ? 'bg-green-500/10 text-green-600 border border-green-500/20 cursor-default' :'bg-primary text-white hover:opacity-90 disabled:opacity-50'}`}
                >
                  {checkingIn ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : todayCheckedIn ? (
                    <><Icon name="CheckCircleIcon" size={18} /> Checked in today ✓</>
                  ) : (
                    <><Icon name="CheckCircleIcon" size={18} /> Check In for Today</>
                  )}
                </button>

                {/* Milestone track */}
                <div>
                  <h3 className="font-700 text-foreground text-sm mb-2 flex items-center gap-2">
                    <Icon name="TrophyIcon" size={15} className="text-amber-500" />
                    Milestones
                  </h3>
                  <MilestoneTrack milestones={milestones} elapsed={daysElapsed(selectedGoal.start_date)} />
                </div>

                {/* Accountability partners */}
                <div>
                  <h3 className="font-700 text-foreground text-sm mb-3 flex items-center gap-2">
                    <Icon name="UserGroupIcon" size={15} className="text-primary" />
                    Accountability Partners ({partners.length})
                  </h3>
                  {partners.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No partners yet. Invite someone to join!</p>
                  ) : (
                    <div className="space-y-2">
                      {partners.map((p) => (
                        <div key={p.id} className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                          <div className="w-9 h-9 rounded-full gradient-teal flex items-center justify-center text-white font-700 text-sm flex-shrink-0">
                            {p.user_profiles?.full_name?.charAt(0) || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-600 text-foreground truncate">
                              {p.user_profiles?.full_name || 'Unknown'}
                              {p.user_id === user?.id && (
                                <span className="ml-1.5 text-[10px] text-primary font-600">(You)</span>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">{p.user_profiles?.email}</p>
                          </div>
                          <Icon name="CheckCircleIcon" size={16} className="text-green-500 flex-shrink-0" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Celebrations feed */}
                <div>
                  <h3 className="font-700 text-foreground text-sm mb-3 flex items-center gap-2">
                    <span>🎉</span>
                    Celebrations
                  </h3>
                  {/* Celebrate a milestone */}
                  {milestones.some((m) => m.status === 'celebrated') && (
                    <div className="flex gap-2 mb-3">
                      <input
                        type="text"
                        value={celebrationMsg}
                        onChange={(e) => setCelebrationMsg(e.target.value)}
                        placeholder="Share a celebration message..."
                        className="flex-1 px-3 py-2 bg-muted border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && celebrationMsg.trim()) {
                            const ms = milestones.find((m) => m.status === 'celebrated');
                            if (ms) handleCelebrate(ms.id);
                          }
                        }}
                      />
                      <button
                        onClick={() => {
                          const ms = milestones.find((m) => m.status === 'celebrated');
                          if (ms) handleCelebrate(ms.id);
                        }}
                        disabled={!celebrationMsg.trim() || !!celebrating}
                        className="px-3 py-2 bg-primary text-white rounded-xl text-sm font-600 hover:opacity-90 disabled:opacity-50 transition-opacity"
                      >
                        🎉
                      </button>
                    </div>
                  )}
                  {celebrations.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No celebrations yet. Reach a milestone to celebrate!</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {celebrations.map((c) => (
                        <div key={c.id} className="flex items-start gap-2.5 p-3 bg-muted rounded-xl">
                          <span className="text-lg flex-shrink-0">{c.emoji}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-600 text-foreground">{c.user_profiles?.full_name || 'Someone'}</p>
                            <p className="text-sm text-muted-foreground">{c.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
